import { COLLECTIONS } from '../../constants/collections';
import { createDocument, getDocument, getAllDocuments, updateDocument, deleteDocument } from './firestoreService';

const getCollectionPath = (tenantId) => `tenants/${tenantId}/${COLLECTIONS.TRANSPORTERS}`;

// Create transporter
export const createTransporter = async (transporterData, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required for creating transporter');

    const data = {
        ...transporterData,
        tenantId,
        performanceMetrics: {
            totalVehicles: 0,
            completedOnTime: 0,
            completedLate: 0,
            averageDelay: 0,
            performanceScore: 100,
            lastCalculated: new Date()
        },
        documents: [],
        notes: [],
        isActive: true,
        isBlacklisted: false,
        blacklistReason: null
    };

    return await createDocument(getCollectionPath(tenantId), data);
};

// Get transporter by ID
export const getTransporter = async (transporterId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await getDocument(getCollectionPath(tenantId), transporterId);
};

// Get all transporters
export const getAllTransporters = async (tenantId, activeOnly = false) => {
    if (!tenantId) throw new Error('Tenant ID is required');

    const conditions = [];

    // tenantId filtering is redundant as we select from the specific tenant collection
    // conditions.push({ type: 'where', field: 'tenantId', operator: '==', value: tenantId });

    if (activeOnly) {
        conditions.push({ type: 'where', field: 'isActive', operator: '==', value: true });
    }

    const results = await getAllDocuments(getCollectionPath(tenantId), conditions);

    // Client-side sort
    return results.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
};

// Update transporter
export const updateTransporter = async (transporterId, updates, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await updateDocument(getCollectionPath(tenantId), transporterId, updates);
};

// Delete transporter
export const deleteTransporter = async (transporterId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await deleteDocument(getCollectionPath(tenantId), transporterId);
};

// Add document to transporter
export const addTransporterDocument = async (transporterId, document, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    const transporter = await getTransporter(transporterId, tenantId);
    const documents = transporter.documents || [];

    documents.push({
        ...document,
        uploadedAt: new Date()
    });

    return await updateDocument(getCollectionPath(tenantId), transporterId, { documents });
};

// Add note to transporter
export const addTransporterNote = async (transporterId, note, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    const transporter = await getTransporter(transporterId, tenantId);
    const notes = transporter.notes || [];

    notes.push({
        ...note,
        createdAt: new Date()
    });

    return await updateDocument(getCollectionPath(tenantId), transporterId, { notes });
};

// Update performance metrics
export const updateTransporterPerformance = async (transporterId, metrics, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await updateDocument(getCollectionPath(tenantId), transporterId, {
        performanceMetrics: {
            ...metrics,
            lastCalculated: new Date()
        }
    });
};

// Blacklist transporter
export const blacklistTransporter = async (transporterId, reason, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await updateDocument(getCollectionPath(tenantId), transporterId, {
        isBlacklisted: true,
        blacklistReason: reason
    });
};

// Remove from blacklist
export const removeFromBlacklist = async (transporterId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await updateDocument(getCollectionPath(tenantId), transporterId, {
        isBlacklisted: false,
        blacklistReason: null
    });
};
